import { useCallback, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import { selectBlockContent, selectSectionIdByBlockId } from '../grid/reduxStore/editorSlice';
import { setActiveSidePanel, setIsReplaceModeActive } from '../grid/reduxStore/blockStyleSettingsSlice';
import type { RootState } from '../grid/reduxStore/Store';
import { GridBlockType } from 'services/entities/BlockTypes';
import { gridDefaultDimensionsOnDrop } from '../shared/gridConfig';
import { getHeightAndWidthFromDataUrl } from '../ImageLibrary/helpers';
import { useCanvasImageUploadHandler } from './useCanvasImageUploadHandler';
import { useNewBlockContentChangedHandler } from './useNewBlockContentChangedHandler';

export function useReplaceImageBlock() {
  const dispatch = useDispatch();
  const { selectedBlockInfoByWrapper, setSelectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { blockId, sectionId } = selectedBlockInfoByWrapper;
  const { handleImageUrlUpload } = useCanvasImageUploadHandler();

  const resolvedSectionId = useSelector((state: RootState) =>
    blockId ? sectionId ?? selectSectionIdByBlockId(state, blockId) : undefined
  );

  const blockContent = useSelector((state: RootState) =>
    blockId && resolvedSectionId ? selectBlockContent(state, resolvedSectionId, blockId) : undefined
  );

  const { handleBlockContentChanged } = useNewBlockContentChangedHandler();

  const replaceImage = useCallback(
    async (rawSrc: string): Promise<void> => {
      if (!blockId || !resolvedSectionId) return;

      const src = await handleImageUrlUpload(rawSrc);
      if (!src) return;

      const { height: imageHeight, width: imageWidth } = await getHeightAndWidthFromDataUrl(src);
      const targetHeight = blockContent?.blockConfig.height ?? gridDefaultDimensionsOnDrop[GridBlockType.IMAGE].height;
      const calculatedWidth = imageHeight > 0 ? Number(((imageWidth / imageHeight) * targetHeight).toFixed(2)) : targetHeight;
      const calculatedHeight = targetHeight;

      if (blockContent?.content === undefined) return;

      handleBlockContentChanged(
        blockId,
        resolvedSectionId,
        blockContent.content,
        src,
        {
          widthPx: blockContent.blockConfig.width,
          heightPx: blockContent.blockConfig.height,
          xAxisPx: blockContent.blockConfig.x,
          yAxisPx: blockContent.blockConfig.y,
        },
        {
          widthPx: calculatedWidth,
          heightPx: calculatedHeight,
          xAxisPx: blockContent.blockConfig.x,
          yAxisPx: blockContent.blockConfig.y,
        }
      );

      setSelectedBlockInfoByWrapper({ blockId, sectionId: resolvedSectionId });
      dispatch(setIsReplaceModeActive(false));
      dispatch(setActiveSidePanel({ type: GridBlockType.IMAGE }));
    },
    [blockId, resolvedSectionId, blockContent, handleImageUrlUpload, handleBlockContentChanged, setSelectedBlockInfoByWrapper, dispatch]
  );

  return { replaceImage };
}
